import { Chip } from '@aibees/shared/components';
import { useMount, useNotify } from '@aibees/shared/hooks';
import { useSearchParams } from '@aibees/shared/router';
import { classnames } from '@aibees/shared/utils';
import { useUpdateWorkspace } from '@aibees/shared/workspace';
import { Divider, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import { useTransferOwnershipLinkMutationApi } from '../../api/use-transfer-ownership-link-mutation-api';
import { useUserManagementContext } from '../../context';
import { TeamMemberTable } from '../../features/team-member-table';
import { useActiveAndPendingMembersCount } from '../../hooks/use-active-and-pending-members-count';
import { useCanInvite } from '../../hooks/use-can-invite';
import { useCheckLimitReached } from '../../hooks/use-check-limit-reached';
import { AddMemberDialog } from '../add-member-dialog';
import { InviteMember } from '../invite-member';
import * as Styled from './styles';
import * as SX from './sx';
import type {
  MembersViewContentProps as Props,
  TransferLinkUrlParams,
} from './types';

export const MembersViewContent = memo(({ className }: Props) => {
  const canInvite = useCanInvite();
  const { inviteFormVisible } = useUserManagementContext();
  const params = useSearchParams<TransferLinkUrlParams>();
  const notify = useNotify();
  const updateWorkspace = useUpdateWorkspace();
  const { seat } = useCheckLimitReached();
  const { activeAndPendingMembersCount: itemLength } =
    useActiveAndPendingMembersCount();
  const confirmTransferOwnershipLink = useTransferOwnershipLinkMutationApi(
    params.link
  );

  useMount(() => {
    if (params['confirm-transfer'] === 'true' && params.link) {
      confirmTransferOwnershipLink
        .mutateAsync({
          captcha: '123456',
        })
        .then((workspace) => {
          updateWorkspace(workspace);
          notify.success(
            `Owner transfer to '${workspace.user.username}' completed successfully.`
          );
        });
    }
  });

  return (
    <Styled.Wrapper
      className={classnames('MembersViewContent', className)}
      variant="outlined"
    >
      <Stack sx={SX.title}>
        <Stack direction="row" alignItems="center" gap={1.25}>
          <Typography variant="body1" fontWeight={700} color="grey.700">
            Team Member{' '}
          </Typography>
          <Chip size="medium" label={`${itemLength}/${seat ?? 'Unlimit'}`} />
        </Stack>

        <AddMemberDialog
          noAccess={!canInvite}
          disabled={inviteFormVisible.value}
          onToggle={inviteFormVisible.onToggle}
        />
      </Stack>
      <Stack sx={SX.stack}>
        {inviteFormVisible.value && (
          <>
            <InviteMember onCloseForm={inviteFormVisible.onFalse} />
            <Divider sx={SX.divider} />
          </>
        )}
        <TeamMemberTable />
      </Stack>
    </Styled.Wrapper>
  );
});

MembersViewContent.displayName = 'MembersViewContent';
